import { useEffect, useMemo, useState } from 'react';
import { Resource } from '../resources';

type LoadingListener = (isLoading: boolean) => void;

class ApiLoadingStore {
  private resourceLoadingCounts: Map<Resource, number> = new Map();
  private listeners: Set<LoadingListener> = new Set();

  /**
   * Increment loading count for a resource
   * Called when an API request starts
   */
  startLoading(resource: Resource) {
    const currentCount = this.resourceLoadingCounts.get(resource) || 0;
    this.resourceLoadingCounts.set(resource, currentCount + 1);

    // Notify listeners (only if transitioning from 0 to 1)
    if (currentCount === 0) {
      this.notifyListeners(true);
    }
  }

  /**
   * Decrement loading count for a resource
   * Called when an API request completes (success or error)
   */
  stopLoading(resource: Resource) {
    const currentCount = this.resourceLoadingCounts.get(resource) || 0;

    if (currentCount <= 0) {
      console.warn(`stopLoading called for resource that wasn't loading:`, resource);
      return;
    }

    const newCount = currentCount - 1;

    if (newCount === 0) {
      this.resourceLoadingCounts.delete(resource);
      this.notifyListeners(false);
    } else {
      this.resourceLoadingCounts.set(resource, newCount);
    }
  }

  /**
   * Check if a resource is currently loading
   */
  isLoading(resource: Resource): boolean {
    return (this.resourceLoadingCounts.get(resource) || 0) > 0;
  }

  /**
   * Check if ANY of the provided resources is loading
   */
  isAnyLoading(resources: Resource[]): boolean {
    return resources.some((resource) => this.isLoading(resource));
  }

  /**
   * Subscribe to loading state changes
   * Returns unsubscribe function
   */
  subscribe(listener: LoadingListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of loading state change
   */
  private notifyListeners(isLoading: boolean) {
    this.listeners.forEach((listener) => listener(isLoading));
  }

  /**
   * Clear all loading states (useful for testing/debugging)
   */
  clearAll() {
    this.resourceLoadingCounts.clear();
    this.notifyListeners(false);
  }
}

export const apiLoadingStore = new ApiLoadingStore();

/**
 * React hook to track loading state for one or more resources
 * @param resourceLoading - Single resource, array of resources, or undefined
 * @returns true if ANY of the specified resources is currently loading
 *
 * @example
 * // Single resource
 * const isLoading = useResourceLoading(resources.updateWorkplace);
 *
 * @example
 * // Multiple resources
 * const isLoading = useResourceLoading([resources.createWorkplace, resources.queryWorkplace]);
 *
 * @example
 * // Optional resource
 * const isLoading = useResourceLoading(condition ? resources.save : undefined);
 */
export function useResourceLoading(resourceLoading?: Resource | Resource[]): boolean {
  const [isLoading, setIsLoading] = useState(false);

  // Normalize to array
  const resources = useMemo(
    () => (resourceLoading ? (Array.isArray(resourceLoading) ? resourceLoading : [resourceLoading]) : []),
    [resourceLoading]
  );

  useEffect(() => {
    if (resources.length === 0) {
      setIsLoading(false);
      return;
    }

    const updateLoadingState = () => {
      setIsLoading(apiLoadingStore.isAnyLoading(resources));
    };

    const unsubscribe = apiLoadingStore.subscribe(updateLoadingState);
    updateLoadingState(); // Set initial state

    return unsubscribe;
  }, [resources]);

  return isLoading;
}